import React, { Component } from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { withFirebase } from '../Firebase';
import * as ROUTES from '../../constants/routes';


const withRouter = (Component) => {
  return (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    return <Component {...props} router={{ navigate, location, params }} />;
  };
};

const withTranslation = (Component) => {
  return (props) => {
    const { t } = useTranslation();
    return <Component {...props} t={t} />;
  };
};

const SignUpPage = () => {
  const { t } = useTranslation();
  
  return (
    <main className='auth-main'>
      <div className='auth-left'>
        <div className='auth-card'>
          <h1>{t('signUp.title')}</h1>
          <SignUpForm />
          <div className='auth-divider'>
            <span>{t('signUp.or_sign_up_with')}</span>
          </div>
          <div className='auth-providers'>
            <SignUpGoogle />
            <SignUpMicrosoft />
          </div>
          <div className='auth-footer'>
            <SignInLink />
          </div>
        </div>
      </div>
      <div className='auth-right'>
        <img 
          src="https://picsum.photos/1200/1200"
          alt={t('signUp.construction_image_alt')}
          loading="lazy"
          sizes="(max-width: 600px) 200px, (max-width: 1200px) 400px, 800px"
        />
      </div>
    </main>
  );
};

const INITIAL_STATE = {
  username: '',
  email: '',
  passwordOne: '',
  passwordTwo: '',
  submitting: false,
  error: null,
};

const SIGN_UP_VERIFICATION_NOTICE_KEY = 'app/signUpVerificationNotice';
const SIGN_UP_VERIFICATION_NOTICE_VALUE = 'emailVerification.signup_notice';
const ERROR_CODE_ACCOUNT_EXISTS = 'auth/email-already-in-use';
const ERROR_CODE_TOO_MANY_REQUESTS = 'auth/too-many-requests';

const ERROR_MSG_ACCOUNT_EXISTS = `
  An account with this E-Mail address already exists.
  Try to login with this account instead. If you think the
  account is already used from one of the social logins, try
  to sign in with one of them. Afterward, associate your accounts
  on your personal account page.
`;

const ERROR_MSG_TOO_MANY_REQUESTS = `
  Firebase has temporarily blocked email/password sign-up from this device due to unusual activity.
  Wait a while before retrying and avoid repeated clicks.
`;

const ERROR_MSG_VERIFICATION_SEND_FAILED = `
  Your account was created, but we could not send the verification email right now.
  You can continue and resend the verification email from the next screen.
`;

class SignUpFormBase extends Component {
  constructor(props) {
    super(props);

    this.state = { ...INITIAL_STATE };
  }

  onSubmit = async event => {
    event.preventDefault();

    const { username, email, passwordOne, submitting } = this.state;
    if (submitting) {
      return;
    }

    this.setState({ submitting: true, error: null });

    try {
      const authUser = await this.props.firebase.doCreateUserWithEmailAndPassword(
        email,
        passwordOne,
      );

      await this.props.firebase.user(authUser.user.uid).set({
        username,
        email,
        createdAt: new Date().toISOString(),
      });

      try {
        await this.props.firebase.doSendEmailVerification();
        sessionStorage.removeItem(SIGN_UP_VERIFICATION_NOTICE_KEY);
      } catch (verificationError) {
        sessionStorage.setItem(
          SIGN_UP_VERIFICATION_NOTICE_KEY,
          SIGN_UP_VERIFICATION_NOTICE_VALUE,
        );
      }

      this.setState({ ...INITIAL_STATE });
      this.props.router.navigate(ROUTES.HOME);
    } catch (error) {
      if (error.code === ERROR_CODE_ACCOUNT_EXISTS) {
        error.message = ERROR_MSG_ACCOUNT_EXISTS;
      }

      if (error.code === ERROR_CODE_TOO_MANY_REQUESTS) {
        error.message = ERROR_MSG_TOO_MANY_REQUESTS;
      }

      this.setState({ error });
    } finally {
      this.setState(prevState => (
        prevState.submitting ? { submitting: false } : null
      ));
    }
  };

  onChange = event => {
    this.setState({ [event.target.name]: event.target.value });
  };


  render() {
    const {
      username,
      email,
      passwordOne,
      passwordTwo,
      submitting,
      error,
    } = this.state;
    const { t } = this.props;

    const isInvalid =
      passwordOne !== passwordTwo ||
      passwordOne === '' ||
      email === '' ||
      username === '';

    return (
      <form onSubmit={this.onSubmit} className='auth-form'>
        {error && (
          <p className='auth-error'>{error.message}</p>
        )}
        <input
          name="username"
          value={username}
          onChange={this.onChange}
          type="text"
          placeholder={t('signUp.username_placeholder')}
          aria-label={t('username')}
          autoComplete="name"
          required
        />
        <input
          name="email"
          value={email}
          onChange={this.onChange}
          type="email"
          placeholder={t('signUp.email_placeholder')}
          aria-label={t('email')}
          autoComplete="username"
          required
        />
        <input
          name="passwordOne"
          value={passwordOne}
          onChange={this.onChange}
          type="password"
          placeholder={t('signUp.password_placeholder')}
          aria-label={t('password')}
          autoComplete="new-password"
          required
        />
        <input
          name="passwordTwo"
          value={passwordTwo}
          onChange={this.onChange}
          type="password"
          placeholder={t('signUp.confirm_password_placeholder')}
          aria-label={t('signUp.confirm_password_placeholder')}
          autoComplete="new-password"
          required
        />
        <button disabled={isInvalid || submitting} type="submit" className={submitting ? 'btn-loading' : undefined}>
          {t('signUp.submit_button')}
        </button>
      </form>
    );
  }
}

class SignUpGoogleBase extends Component {
  constructor(props) {
    super(props);

    this.state = { error: null, submitting: false };
  }

  onSubmit = event => {
    event.preventDefault();
    if (this.state.submitting) return;

    this.setState({ submitting: true, error: null });

    this.props.firebase
      .doSignInWithGoogle()
      .then(socialAuthUser => {
        // Create or update a user in your Firebase Realtime Database.
        // Using `update` instead of `set` will preserve existing data like roles.
        if (socialAuthUser.additionalUserInfo.isNewUser) {
          return this.props.firebase.user(socialAuthUser.user.uid).set({
            username: socialAuthUser.user.displayName,
            email: socialAuthUser.user.email,
            roles: {}, // Set default roles for new users if needed
            createdAt: new Date().toISOString(),
          });
        }
        return this.props.firebase.user(socialAuthUser.user.uid).update({
          username: socialAuthUser.user.displayName,
          email: socialAuthUser.user.email,
        });
      })
      .then(() => {
        this.setState({ error: null });
        this.props.router.navigate(ROUTES.HOME);
      })
      .catch(error => {
        if (error.code === ERROR_CODE_ACCOUNT_EXISTS) {
          error.message = this.props.t('signUp.error_account_exists');
        }

        this.setState({ error });
      })
      .finally(() => {
        this.setState(prevState => (
          prevState.submitting ? { submitting: false } : null
        ));
      });
  };

  render() {
    const { error, submitting } = this.state;
    const { t } = this.props;

    return (
      <div className="inline">
        <button className={'gsi-material-button' + (submitting ? ' btn-loading' : '')} onClick={this.onSubmit} type="button" disabled={submitting}>
          <div className="gsi-material-button-state"></div>
          <div className="gsi-material-button-content-wrapper">
            <div className="gsi-material-button-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 48 48"
                style={{ display: "block" }}
              >
                <path
                  fill="#EA4335"
                  d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
                ></path>
                <path
                  fill="#4285F4"
                  d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
                ></path>
                <path
                  fill="#FBBC05"
                  d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
                ></path>
                <path
                  fill="#34A853"
                  d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
                ></path>
                <path fill="none" d="M0 0h48v48H0z"></path>
              </svg>
            </div>
            <span className="gsi-material-button-contents">{t('signUp.sign_up_google')}</span>
            <span style={{ display: "none" }}>{t('signUp.sign_up_google')}</span>
          </div>
        </button>
        {error && (
          <p className='auth-error'>{error.message}</p>
        )}
      </div>
    );
  }
}

class SignUpMicrosoftBase extends Component {
  constructor(props) {
    super(props);

    this.state = { error: null, submitting: false };
  }

  onSubmit = event => {
    event.preventDefault();
    if (this.state.submitting) return;

    this.setState({ submitting: true, error: null });

    this.props.firebase
      .doSignInWithMicrosoft()
      .then(socialAuthUser => {
        // Create or update a user in your Firebase Realtime Database.
        // Using `update` instead of `set` will preserve existing data like roles.
        if (socialAuthUser.additionalUserInfo.isNewUser) {
          return this.props.firebase.user(socialAuthUser.user.uid).set({
            username: socialAuthUser.user.displayName,
            email: socialAuthUser.user.email,
            roles: {}, // Set default roles for new users if needed
            createdAt: new Date().toISOString(),
          });
        }
        return this.props.firebase.user(socialAuthUser.user.uid).update({
          username: socialAuthUser.user.displayName,
          email: socialAuthUser.user.email,
        });
      })
      .then(() => {
        this.setState({ error: null });
        this.props.router.navigate(ROUTES.HOME);
      })
      .catch(error => {
        if (error.code === ERROR_CODE_ACCOUNT_EXISTS) {
          error.message = this.props.t('signUp.error_account_exists');
        }

        this.setState({ error });
      })
      .finally(() => {
        this.setState(prevState => (
          prevState.submitting ? { submitting: false } : null
        ));
      });
  };

  render() {
    const { error, submitting } = this.state;
    const { t } = this.props;

    return (
      <div className="inline">
        <button className={'microsoft-button' + (submitting ? ' btn-loading' : '')} onClick={this.onSubmit} type="button" disabled={submitting}>
          <svg xmlns="http://www.w3.org/2000/svg" width="21" height="21" viewBox="0 0 21 21">
            <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
            <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
            <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
            <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
          </svg>
          <span>{t('signUp.sign_up_microsoft')}</span>
        </button>

        {error && (
          <p className='auth-error'>{error.message}</p>
        )}
      </div>
    );
  }
}

const SignUpLink = () => {
  const { t } = useTranslation();
  return (
    <p>
      {t('signUp.dont_have_account')} <Link to={ROUTES.SIGN_UP}>{t('sign_up')}</Link>
    </p>
  );
};

const SignInLink = () => {
  const { t } = useTranslation();
  return (
    <p>
      {t('signUp.already_have_account')} <Link to={ROUTES.SIGN_IN}>{t('sign_in')}</Link>
    </p>
  );
};

const SignUpForm = withTranslation(withRouter(withFirebase(SignUpFormBase)));
const SignUpGoogle = withTranslation(withRouter(withFirebase(SignUpGoogleBase)));
const SignUpMicrosoft = withTranslation(withRouter(withFirebase(SignUpMicrosoftBase)));

export default SignUpPage;

export { SignUpForm, SignUpGoogle, SignUpMicrosoft, SignUpLink };
